export interface StatisticsFilters {
  categoryIds: string[];
  subCategoryId: string | null;
  teamIds: string[];
  populationIds: string[];
}

export const EMPTY_FILTERS: StatisticsFilters = {
  categoryIds: [],
  subCategoryId: null,
  teamIds: [],
  populationIds: [],
};

/** Matches server `KpiBreakdownResult` (linko `getKpiBreakdown`). */
export interface KpiBreakdownData {
  populations: { id: string; name: string; count: number }[];
  teams: { id: string; name: string; count: number }[];
  categorySubcategories: {
    categoryId: string;
    categoryName: string;
    subCategoryId: string | null;
    subCategoryName: string;
    count: number;
  }[];
}

export interface KpiAvgTimeDistributionBucket {
  bucketKey: number;
  bucketLabel: string;
  count: number;
}

export type SmartInsightType =
  | "staleUnfull"
  | "bloatedCategory"
  | "lowFillRate"
  | "upcomingDeadlineLowFill"
  | "unbalancedTeams";

export interface SmartInsight {
  type: SmartInsightType;
  severity: "warning" | "info";
  titleHe: string;
  descriptionHe: string;
  entityId: string;
  entityName: string;
  numericValue: number;
  targetOpportunityId: string;
}

export interface StatisticsQueryFilters {
  organizationId: string;
  categoryIds?: string[];
  subCategoryId?: string;
  teamIds?: string[];
  populationIds?: string[];
}

export function buildStatisticsQueryFilters(
  organizationId: string,
  filters: StatisticsFilters,
): StatisticsQueryFilters {
  return {
    organizationId,
    categoryIds: filters.categoryIds.length ? filters.categoryIds : undefined,
    subCategoryId: filters.subCategoryId ?? undefined,
    teamIds: filters.teamIds.length ? filters.teamIds : undefined,
    populationIds: filters.populationIds.length
      ? filters.populationIds
      : undefined,
  };
}

/** Aligns with server `getDailyTrends` response. */
export interface StatisticsDailyTrendDayRow {
  date: string;
  volunteerRegistrations: number;
  openedMissions: number;
}

export interface StatisticsDailyTrendsResult {
  days: StatisticsDailyTrendDayRow[];
}

export interface StatisticsDailyTrendsQueryBody extends StatisticsQueryFilters {
  dateFrom: string;
  dateTo: string;
}

/** Aligns with server `getHourlyTrends` response. */
export interface StatisticsHourlyTrendHourRow {
  hour: number;
  hourLabel: string;
  volunteerRegistrations: number;
  openedMissions: number;
}

export interface StatisticsHourlyTrendsResult {
  hours: StatisticsHourlyTrendHourRow[];
}

export interface StatisticsHourlyTrendsQueryBody
  extends StatisticsQueryFilters {
  date: string;
}

export interface ManagerDailyMatchesDayRow {
  date: string;
  matchCount: number;
}

export interface ManagerDailyMatchesSeries {
  managerId: string;
  managerName: string;
  days: ManagerDailyMatchesDayRow[];
  total: number;
}

export interface ManagerDailyMatchesResult {
  managers: ManagerDailyMatchesSeries[];
  canViewAllManagers: boolean;
  canManageManualMatches: boolean;
}

export interface ManagerDailyMatchesQueryBody {
  organizationId: string;
  dateFrom: string;
  dateTo: string;
}

export interface AddManualMatchBody {
  organizationId: string;
  matchCount: number;
  matchedAt?: string;
  addToBank?: boolean;
  createdByManagerId?: string;
  note?: string;
}

export interface MatchBankEntry {
  id: string;
  matchCount: number;
  note: string;
  createdAt: string;
  createdByManagerId: string;
  createdByManagerName: string | null;
}

export interface AssignManualMatchDateBody {
  organizationId: string;
  id: string;
  matchedAt: string;
  matchCount: number;
}

export type StatisticsAgeBucketKey =
  | "under18"
  | "18_24"
  | "25_34"
  | "35_44"
  | "45_54"
  | "55_64"
  | "65plus";

export interface StatisticsVolunteerAgeGenderRow {
  bucketKey: StatisticsAgeBucketKey;
  bucketLabel: string;
  gender: string;
  count: number;
}

export type StatisticsVolunteerRepeatBucketKey =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6plus";

export interface StatisticsVolunteerRepeatBucketRow {
  bucketKey: StatisticsVolunteerRepeatBucketKey;
  bucketLabel: string;
  count: number;
}

export interface StatisticsVolunteerInsightsResult {
  ageGender: StatisticsVolunteerAgeGenderRow[];
  repeat: StatisticsVolunteerRepeatBucketRow[];
  /** מצטרפים חדשים לארגון בטווח התאריכים (שיוך VOLUNTEER). */
  newVolunteersInRange: number;
}

export interface StatisticsVolunteerInsightsQueryBody extends StatisticsQueryFilters {
  dateFrom?: string;
  dateTo?: string;
}

export type StatisticsGroupPreferredRegionKey =
  | "NORTH"
  | "CENTER"
  | "SOUTH"
  | "unspecified";

export interface StatisticsGroupPreferredRegionRow {
  regionKey: StatisticsGroupPreferredRegionKey;
  regionLabel: string;
  count: number;
}

export type StatisticsGroupSizeBucketKey =
  | "up_to_10"
  | "10_20"
  | "20_30"
  | "30_40"
  | "40_50"
  | "50plus";

export interface StatisticsGroupSizeBucketRow {
  bucketKey: StatisticsGroupSizeBucketKey;
  bucketLabel: string;
  count: number;
}

export type StatisticsGroupRepeatBucketKey = StatisticsVolunteerRepeatBucketKey;

export interface StatisticsGroupRepeatBucketRow {
  bucketKey: StatisticsGroupRepeatBucketKey;
  bucketLabel: string;
  count: number;
}

export interface StatisticsGroupInsightsResult {
  preferredRegions: StatisticsGroupPreferredRegionRow[];
  sizeBuckets: StatisticsGroupSizeBucketRow[];
  repeat: StatisticsGroupRepeatBucketRow[];
  totalGroups: number;
  totalMembers: number;
  approvedParticipationsInRange: number;
  newGroupsInRange: number;
}

export interface StatisticsGroupInsightsQueryBody extends StatisticsQueryFilters {
  dateFrom?: string;
  dateTo?: string;
}

export interface StatisticsGeographicPoint {
  lat: number;
  lng: number;
  weight: number;
}

export interface StatisticsGeographicRegionRow {
  regionKey: string;
  regionLabel: string;
  volunteerCount: number;
  openOpportunityCount: number;
  demandGap: number;
}

export interface StatisticsGeographicInsightsResult {
  volunteers: StatisticsGeographicPoint[];
  openOpportunities: StatisticsGeographicPoint[];
  volunteerCount: number;
  openOpportunityCount: number;
  volunteersWithoutLocation: number;
  openOpportunitiesWithoutLocation: number;
  aggregationCellKm: number;
  districtComparison: StatisticsGeographicRegionRow[];
  subDistrictComparison: StatisticsGeographicRegionRow[];
}
